import dayjs from 'dayjs';
import { Dispatch } from 'redux';
import { Result } from '..';
import { storageItem } from '../dom-utils';
import { getOriginalJSON } from '../legacy';
import * as model from '../model';
import { ClidObjectType, MonitorGroup, SERVER_DATE_FOMATE } from '../model';
import { actions } from '../store';
import { MediaTypesFilter } from '../store/reducers/localSettings';
import { contactSearchColumns, extensionSearchColumns, queueSearchColumns, taskSearchColumns } from './contact-control';
import { AsyncBookitResponse, AsyncResponse } from './transcriptions';

const prevId = storageItem<string>(sessionStorage, 'prevId')


let ClientId = prevId.get()
export function setClientId(id: string) {
  ClientId = id
  prevId.set(id)
}
export function hasClientId() { return Boolean(ClientId) }

let BaseUrl = '/'

const JSON = getOriginalJSON()

export function setBaseUrl(url: string) {
  if (!url.endsWith('/')) {
    throw new Error(`'${url}' should end with /`)
  }
  BaseUrl = url
}

/** /uc/Teams/blabla */
export function getUrl(url: string, absolute = true) {
  const relative = BaseUrl + url.replace(/^\/+/, '')
  return absolute ? window.location.origin + relative : relative
}

/** /uc/blabla */
export function getApiUrl(url: string, absolute = true) {
  const relative = BaseUrl + '../' + url.replace(/^\/+/, '')
  return absolute ? window.location.origin + relative : relative
}

function ucFetch(url: string, init?: RequestInit) {
  return fetch(url, {
    ...init,
    headers: {
      ...(init && init.headers),
      'ipfx-id': ClientId!,
    },
    credentials: 'include',
  })
}

export class HttpError extends Error {
  public readonly status: number
  public readonly statusText: string
  public readonly body: string

  constructor(status: number, statusText: string, body: string) {
    super(`${status} ${statusText}`)

    this.name = 'HttpError'
    this.status = status
    this.statusText = statusText
    this.body = body

    Object.setPrototypeOf(this, HttpError.prototype)
  }
}

function parseUcStatus(body: string) {
  const result = /^(\d{3})([^-]*)(?:-([\s\S]*))?$/.exec(body)
  if (!result) {
    throw new Error('Cannot parse ' + body)
  }

  const [, status, statusText, text] = result
  return { status: parseInt(status), statusText, text }
}

export function toLowerJson(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(toLowerJson)
  } if (obj && typeof obj === 'object') {
    const newObj: Record<string, any> = {}
    for (const key of Object.keys(obj)) {
      newObj[key[0].toLowerCase() + key.substr(1)] = toLowerJson(obj[key])
    }
    return newObj
  } else {
    return obj
  }
}

export function parseJson<T>(json: string) {
  if (!json) { return undefined! as T }
  return toLowerJson(JSON.parse(json)) as T
}

async function grid<T = any>(query: {}) {
  const params = new URLSearchParams({
    _search: 'false',
    nd: Date.now().toString(),
    rows: '-1',
    page: '1',
    gs: JSON.stringify(query),
  })

  const url = getApiUrl('/Handlers/Grid.ashx?' + params)
  console.log('>>> ' + url)

  const resp = await ucFetch(url)
  if (!resp.ok) { throw new HttpError(resp.status, resp.statusText, await resp.text()) }

  if (!/^text\/plain[;$]/.test(resp.headers.get('Content-Type') || '')) {
    const { status, statusText, text } = parseUcStatus(await resp.text())
    throw new HttpError(status, statusText, text)
  }

  const grid = parseJson<GridData<T>>(await resp.text())
  return grid
}

export type HandlerBody = Record<string, string | undefined> & { command: string } | Record<string, string | undefined> & { client: string }

export async function handler(url: string, body: HandlerBody, ucStatus = true, method = 'POST') {
  if (method === 'GET') {
    if (url.indexOf('?') === -1)
      url += '?'
  }

  body = { ...body }
  for (const key of Object.keys(body)) {
    const value = body[key]
    if (value === undefined) {
      delete body[key]
    } else {
      if (method === 'GET') {
        url += '&' + key + '=' + encodeURIComponent(value)
      }
    }
  }

  const x = getApiUrl(url)
  console.log('>>> ' + url + JSON.stringify(body))

  const resp = await ucFetch(x, { method: method, body: (method === 'GET') ? null : new URLSearchParams(body as Record<string, string>) })
  if (!resp.ok) { throw new HttpError(resp.status, resp.statusText, await resp.text()) }

  if (!ucStatus) { return await resp.text() }

  const { status, statusText, text } = parseUcStatus(await resp.text())
  if (status !== 200) { throw new HttpError(status, statusText, text) }
  return text
}

export async function extHandlerRaw(body: HandlerBody, ucStatus = true) {
  return await handler('/Handlers/ExtensionHandler.ashx', body, ucStatus)
}

async function extHandler<T = any>(body: HandlerBody, ucStatus = true) {
  return parseJson<T>(await extHandlerRaw(body, ucStatus))
}

export async function deviceHandlerRaw(body: HandlerBody, ucStatus = true) {
  return await handler('/Handlers/Dialogs/DeviceGroup.ashx', body, ucStatus)
}

/*
async function deviceHandler<T = any>(body: HandlerBody, ucStatus = true) {
  return parseJson<T>(await extHandlerRaw(body, ucStatus))
}
*/

export function gridQuery<T = any>(section: string, subSection: string, server: string, subscription: model.EventSubscription, id?: string, command?: string, extension?: string, searchText?: string, pageSize?: number, searchColumns?: string[], pageIndex?: number, orderColumn?: string, orderDesc?: boolean, socketId?: string) {
  return grid<T>({
    Section: section,
    SubSection: subSection,
    ServerName: server,
    Id: id,
    PageSize: !pageSize ? -1 : pageSize,
    PageIndex: !pageIndex ? 0 : pageIndex,
    Clean: subscription,
    CommandText: command,
    ExtensionId: extension,
    SearchText: searchText,
    SearchProperties: !searchColumns ? null : searchColumns,
    OrderColumn: orderColumn ? orderColumn : '',
    OrderDirection: orderDesc ? 'desc' : 'asc',
    SocketId: socketId ? socketId : '',
  })
}

export interface GridData<T> {
  total: number
  page: number
  records: number
  rows: T[],
  userdata: { error: string, data: string }
}

export const MinDate: number = -62135596800000

export function parseMsDate(date: string | undefined, skipMinDate = false) {
  if (!date) { return }


  const [, val] = /^\/Date\((-?\d+)\)\/$/.exec(date)!

  if (skipMinDate && val === MinDate.toString()) { return }
  return parseInt(val)
}

export function parseOaDate(oaDate: number) {
  const days = parseInt(oaDate.toString());
  // Treat decimal part as part of 24hr day, always +ve
  const ms = Math.abs((oaDate - days) * 8.64e7);
  // Add days and add ms
  return dayjs(new Date(1899, 11, 30 + days, 0, 0, 0, ms)).valueOf();
}

export function parseStringDate(date: string) {
  if (!date) { return }
  return dayjs(date, SERVER_DATE_FOMATE).valueOf()
}

export function secondsToTimestamp(seconds: number) {
  return Date.now() - seconds * 1000
}

export async function getSquads() {
  return (await gridQuery('teams', '', '*', model.EventSubscription.Add)).rows.map(mapSquad)
}

function mapSquad(row: any): model.Squad {
  return {
    id: row.id.toString(),
    server: row.serverName,
    type: 'squad',
    description: row.description,
  }
}

// export function getQueueStatus(status: number): QueueStatus {
//   const result: QueueStatus | undefined =
//     status === 0 ? 'scheduled' :
//     status === 1 ? 'open' :
//     status === 2 ? 'closed' :
//     status === 4 ? 'no agents' :
//     status === 6 ? 'holiday' :
//     undefined

//   if (!result) { throw new Error('Unknown status: ' + status) }
//   return result
// }

export async function getQueueAgents(entityId: model.QueueOrSquadId) {
  const { server, id } = entityId

  const data = model.isQueueId(entityId)
    ? await gridQuery('queues', 'queue_agents', server, model.EventSubscription.Add, id)
    : await gridQuery('teams', 'extensions', '', model.EventSubscription.Add, id)

  return data.rows.map<model.Agent>(row => ({
    id: row.extensionNumber,
    server: row.serverName,
    name: `${row.firstName} ${row.lastName}`,
    presence: row.presence,
    returnTime: parseMsDate(row.regionReturnTime, true),
    utcReturnTime: row.utcReturnTime ? parseMsDate(row.utcReturnTime, true) : undefined,
    startTime: parseMsDate(row.presenceStartTime, true),
    // note: row.operatorNotes,
    answered: row.ansCount,
    talkTime: row.talkTimeTotal,
    media: [],
    enabled: row.enabled,
    deviceGroupType: parseInt(row.currentDeviceGroupType),
    awayFromDesk: row.awayFromDesk,
    waitingForWrapup: row.waitingForWrapup,
    tempLogout: row.tempLogout,
    status: row.status,
    subStatus: row.statusReason,
    breakCode: row.breakCode,
    access: row.access,
    skill: row.skillLevel,
    entryTime: row.entryTime,
    entryCount: row.entryCount,
    extendedAvailablePresence: row.extendedAvailablePresence,
    department: row.department,
    regionId: row.regionId,
    expertise: row.expertise,
    operatorNotes: row.operatorNotes,
    activity: row.activity === '' ? [] : row.activity.split(',').filter((x: string) => x !== ''),
  }))
}

export async function getQueueMedia(entityId: model.QueueOrSquadId) {
  const { server, id } = entityId
  const data = model.isQueueId(entityId)
    ? await gridQuery('currentcalls', 'queues', server, model.EventSubscription.Add, id)
    : await gridQuery('teams', 'calls', '', model.EventSubscription.Add, id)

  return data.rows.map<model.Media & { agent: model.AgentKey }>(row => ({
    id: row.callId,
    deviceId: row.deviceId,
    status: row.status,
    clid: row.cLID,
    clidDescription: row.cLIDDescription,
    connectedParty: row.connectedParty,
    server: entityId.server,
    queueId: row.queue,
    queueIdDescription: row.queueIdDescription,
    dnis: row.dNIS,
    dnisDescription: row.dNISDescription,
    fullName: row.fullname,
    elapsedTime: secondsToTimestamp(row.elapsedTime),
    mediaType: row.objectType,
    wrapupFolder: row.wrapupFolderKey,
    preferredExtn: row.preferredExtn,
    forwardFrom: row.forwardFrom,
    forwardReason: row.forwardReason,
    callType: row.callType,
    isQueueCall: row.isQueueCall,
    deviceType: row.deviceType,
    recordStatus: row.recordStatus,
    reason: row.reason,
    notes: row.notes,
    urgency: row.urgency,
    extensionId: row.extensionId,
    deviceDescription: row.deviceDescription,
    //devices : [0], // TODO row.devices array of CallDevice
    subject: row.subject,
    extensionsWaitingForWrapup: row.extensionsWaitingForWrapupText || [],
    callTransactionId: row.callTransactionId,
    queuingTime: row.queuingSeconds,
    heldTime: row.heldSeconds,
    talkTime: row.talkSeconds,
    wrapupTime: row.wrapupSeconds,
    inVmRecordingMessage: row.inVmRecordingMessage,
    preferredExtnDescription: row.preferredExtnDescription,
    clidAccountCode: row.cLIDAccountCode,
    clidNumberType: row.cLIDNumberType,
    clidObjectKey: row.cLIDObjectKey,
    clidObjectType: row.cLIDObjectType,
    sourceApp: row.sourceApp,
    lastUpdateTime: Date.now(),
    isRecording: false,
    voicemailPort: '',
    voicemailPortIsConnected: false,
    voicemailPortIsSuppresed: 'allowed',
    hide: false,
    minimised: 0,
    taskId: '',
    url: false,
    mediaItemId: row.mediaItemId,
    extensionEmail: row.extensionEmail,
    agent: {
      queue: row.queue,
      server,
      agent: row.extensionId || null,
    },
    dtmf: false,
    window: false,
  }))
}

export async function getSearchMediaTasks({ server, id }: model.Identifier, text: string) {
  return (await gridQuery('tasks', 'search', server, model.EventSubscription.Add, '', '', id, text, 50, taskSearchColumns, undefined, 'Description', false)).rows.map<model.Task>((row => {
    const key: string[] = row.stringKey.split('|')
    const id = key[1]

    return {
      taskId: id,
      createdAtUtc: 0,
      createdBy: '',
      subject: row.description,
      contactId: row.data[1],
      lookupId: row.data[0],
      stateId: row.data[2],
      items: undefined,
    }
  }))
}

export async function getMediaTasks({ server, id }: model.Identifier, contactId: string) {
  return (await gridQuery('tasks', '', server, model.EventSubscription.Add, contactId, '', id, '', 200)).rows.map<model.Task>(row => ({
    taskId: row.taskId,
    createdAtUtc: parseMsDate(row.createdAtUtc) || 0,
    createdBy: row.createdBy,
    subject: row.subject,
    contactId: row.contactId,
    lookupId: row.lookupId,
    stateId: row.stateId,
    items: undefined,
  }))
}

export async function getMediaTaskItemsUnclassifed({ server, id }: model.Identifier, contactId: string, excludeItemId: string) {
  return (await gridQuery('tasks', 'contact', server, model.EventSubscription.None, contactId, excludeItemId, id, '', 200)).rows.map<model.TaskItem>(row => ({
    itemId: row.itemId,
    mediaTypeId: row.mediaTypeId,
    action: row.action,
    dateTimeUtc: parseMsDate(row.dateTimeUtc) || 0,
    subject: row.subject,
    stateId: row.stateId,
    lookupId: row.lookupId,
  }))
}

export async function getMediaTaskItems({ server, id }: model.Identifier, taskId: string) {
  return (await gridQuery('tasks', 'items', server, model.EventSubscription.Add, taskId, '', id, '', 200)).rows.map<model.TaskItem>(row => ({
    itemId: row.itemId,
    mediaTypeId: row.mediaTypeId,
    action: row.action,
    dateTimeUtc: parseMsDate(row.dateTimeUtc) || 0,
    subject: row.subject,
    stateId: row.stateId,
    lookupId: row.lookupId,
  }))
}

export async function getMediaItemsUnclassifed({ server, id }: model.Identifier, mediaType: model.MediaType, value: string) {
  return (await gridQuery('tasks', 'unclassified', server, model.EventSubscription.None, '', mediaType, id, value, 200)).rows.map<model.TaskItem>(row => ({
    itemId: row.itemId,
    mediaTypeId: row.mediaTypeId,
    action: row.action,
    dateTimeUtc: parseMsDate(row.dateTimeUtc) || 0,
    subject: row.subject,
    stateId: row.stateId,
    lookupId: row.lookupId,
  }))
}

export async function getExtensions({ server, id }: model.Identifier, regionId: string, page: number) {
  const grid = await gridQuery('directory', 'regions', server, model.EventSubscription.Add, regionId, '', id, '', 50, undefined, page)

  return {
    ...grid, rows: grid.rows.map<model.Extension>(row => ({
      id: row.id,
      server: row.serverName,
      name: row.firstName + ' ' + row.lastName,
      presence: row.presence,
      breakCode: parseInt(row.breakcode),
      returnTime: parseMsDate(row.regionReturnTime, true),
      startTime: parseMsDate(row.presenceStartTime, true),
      deviceGroupType: parseInt(row.currentDeviceGroupType),
      status: parseInt(row.status),
      subStatus: parseInt(row.statusReason),
      awayFromDesk: row.awayFromDesk,
      waitingForWrapup: row.waitingForWrapup,
      extendedAvailablePresence: row.extendedAvailablePresence,
      associateNumber: row.associateNumber,
      email: row.email,
      homeNumber: row.home,
      mobileNumber: row.mobile,
      officeNumber: row.businessNumber,
      otherNumber: row.otherNumber,
      isMobileNumberVisible: row.isMobileNumberVisible,
      isHomeNumberVisible: row.isHomeNumberVisible,
      isOtherNumberVisible: row.isHomeNumberVisible,
      tempLogout: row.tempLogout,
      department: row.department,
      expertise: row.expertise,
      regionId: row.regionId,
      operatorNotes: row.operatorNotes,
      activity: row.activity === '' ? [] : row.activity.split(',').filter((x: string) => x !== ''),
    })),
  }
}

export async function getExtensionsByIds({ server, id }: model.Identifier, extensionId: model.Identifier[], page: number) {
  let ids = ''
  extensionId.forEach(x => {
    ids += x.server + '|' + x.id + ','
  });
  const grid = await gridQuery('directory', 'list', server, model.EventSubscription.Add, '', ids, id, '', 50, undefined, page)

  return {
    ...grid, rows: grid.rows.map<model.Extension>(row => ({
      id: row.id,
      server: row.serverName,
      name: row.firstName + ' ' + row.lastName,
      presence: row.presence,
      breakCode: parseInt(row.breakcode),
      returnTime: parseMsDate(row.regionReturnTime, true),  
      startTime: parseMsDate(row.presenceStartTime, true),
      deviceGroupType: parseInt(row.currentDeviceGroupType),
      status: parseInt(row.status),
      subStatus: parseInt(row.statusReason),
      awayFromDesk: row.awayFromDesk,
      waitingForWrapup: row.waitingForWrapup,
      extendedAvailablePresence: row.extendedAvailablePresence,
      associateNumber: row.associateNumber,
      email: row.email,
      homeNumber: row.home,
      mobileNumber: row.mobile,
      officeNumber: row.businessNumber,
      otherNumber: row.otherNumber,
      isMobileNumberVisible: row.isMobileNumberVisible,
      isHomeNumberVisible: row.isHomeNumberVisible,
      isOtherNumberVisible: row.isHomeNumberVisible,
      tempLogout: row.tempLogout,
      department: row.department,
      expertise: row.expertise,
      regionId: row.regionId,
      operatorNotes: row.operatorNotes,
      activity: row.activity === '' ? [] : row.activity.split(',').filter((x: string) => x !== ''),
    })),
  }
}

export async function getExtensionFavs({ server, id }: model.Identifier, folderId: string, page: number) {
  const grid = await gridQuery('directory', 'favourites', server, model.EventSubscription.Add, folderId, '', id, '', 50, undefined, page)

  return {
    ...grid, rows: grid.rows.map<model.Extension>(row => ({
      id: row.id,
      server: row.serverName,
      name: row.description,
      presence: row.presence,
      breakCode: parseInt(row.breakCode),
      returnTime: parseMsDate(row.regionReturnTime, true),
      startTime: parseMsDate(row.presenceStartTime, true),
      deviceGroupType: parseInt(row.currentDeviceGroupType),
      status: parseInt(row.status),
      subStatus: parseInt(row.statusReason),
      awayFromDesk: row.awayFromDesk,
      waitingForWrapup: row.waitingForWrapup,
      extendedAvailablePresence: row.extendedAvailablePresence,
      associateNumber: row.associateNumber,
      email: row.email,
      homeNumber: row.homePhone,
      mobileNumber: row.mobilePhone,
      officeNumber: row.businessPhone,
      otherNumber: row.otherNumber,
      isMobileNumberVisible: row.isMobileNumberVisible,
      isHomeNumberVisible: row.isHomeNumberVisible,
      isOtherNumberVisible: row.isHomeNumberVisible,
      tempLogout: row.tempLogout,
      department: row.department,
      expertise: row.expertise,
      regionId: row.regionId,
      operatorNotes: row.operatorNotes,
      activity: row.activity === '' ? [] : row.activity.split(',').filter((x: string) => x !== ''),
    })),
  }
}

export interface Button {
  description: string
  data?: string[]
  nonIdentifyingData: string
  groupListId: string
  entityId: string
  serverName: string
  entityType: number
  // sortType: number
}

export interface ApplicationSetting {
  applicationSettingId: string
  applicationId: string
  settingType: string
  identifier: string
  value: string
  extensionId: string
  dirty: model.Dirty
  lastUpdated: string
}

export interface Config {
  version: string
  ipAddress: string
  clientId: string
  serverName: string
  extension: string

  firstName: string
  lastName: string
  fullName: string

  officeExtension: string
  associateNumber: string
  mobile: string
  homeNumber: string
  otherNumber: string
  screenCapture: boolean
  email: string

  sessionPingTimeInterval: number
  locations: Array<{
    id: number
    description: string,
    defaultReturnTimeId: number,
  }>
  breakCodes: model.BreakCode[]

  currentStatus: number
  currentSubStatus: number
  currentPresence: number
  currentBreakcode: number
  currentRegionReturnTime: string
  currentDeviceGroupType: number
  currentAwayFromDesk: boolean
  waitingForWrapup: boolean
  tempLogout: boolean
  presenceStartTime: string

  extendedAvailablePresence: boolean
  canReceiveVoicemail: boolean

  userRegion: { serverName: string, id: string, name: string }
  regions: {
    serverName: string,
    id: string,
    name: string,
    countryCode: string,
    localAreaCode: string,
    nationalAccessCode: string,
    localNumberLength: number,
    timeBias: number,
  }[]

  currentDeviceGroup: model.DeviceGroup
  primaryDevice: model.ExtensionDevice
  devices: model.ExtensionDevice[]
  baseUserLevel: model.UserLevel
  featureClassId: number
  isLync: boolean
  isLicensedCallRecording: boolean
  isTeamsPbx: boolean
  isScreenRecording: boolean

  features: model.Features

  vmExtensions: string[]

  agentDisplayName: ApplicationSetting
  wrapupSingleSelect: boolean

  agentStats: model.AgentStatistic | null
  agentLimits: model.AgentLimt[]

  dateTimeLongFormat: string
  dateTimeShortFormat: string
  shortTimeFormat: string
  longTimeFormat: string
  shortDateFormat: string
  longDateFormat: string

  command: model.CommandInfo[]

  vMPortExtensions: string[]

  rescheduleWrapupDuration: number

  defaultWorkTime: number

  requestId: string
  sessionId: string
  imEnabled: boolean
  imAllowBlockedMessagesSentAsEmails: boolean
  imBlockMessageInLocation: boolean

  taskStates: model.TaskState[]

  regionId: string
  department: string
  expertise: string
  siteSpecificExpertise: boolean

  shortcuts: Button[]

  applicationSettings: ApplicationSetting[]

  callAssistants: model.CallAssistant[]

  canMonitor: boolean
  teamsEnhancedCti: boolean

  preReleaseFeatures: string

  turnServer: string
  turnServerUdp: string
  turnServerTcp: string
  janus: string
  realm: string

  operatorNotes: string
  activity: string
  teamsEnhanced: boolean
  emailAttachmentPreview: string[]

  myQueues: { key: string, value: string }[]

  imageDateTime?: String

  clientDebugging: boolean
  transcriptionEnabled: boolean
  mediaQueueTwitter: boolean
  mediaQueueFacebook: boolean
  mediaQueueEmail: boolean
  mediaQueueWebChat: boolean
  autoLocnExpiry: boolean

  monitorGroups: MonitorGroup[]
  localServerTimeZoneBias: number
}

export async function getConfig() {
  const config = JSON.parse(await extHandlerRaw({ command: '17APD' })) as {}
  return {
    original: config as any,
    config: toLowerJson(config) as Config,
  }
}

export async function ping(clientId: string) {
  await extHandlerRaw({ command: 'ping', id: clientId })
}

export async function getDeviceGroups(server: string, extension: string): Promise<[[string], model.DeviceGroup[]]> {
  let text = await handler('/Handlers/Dialogs/DeviceGroup.ashx', {
    command: 'get',
    servername: server,
    extensionId: extension,
  });

  return JSON.parse(text)
}


export function processApiError(ex: Error, dispatch: Dispatch) {

  console.error(ex)

  if (ex instanceof HttpError && ex.status === 505) {
    dispatch(actions.sessionExpired())
  } else {
    throw ex
  }
}

export async function setDefaultPresence({ server, id }: model.Identifier, presence: model.Presence, type?: model.DeviceGroupType, breakCode?: number) {
  await handler('/Handlers/Dialogs/Presence.ashx', {
    command: 'set-presence',
    servername: server,
    id: id,
    location: presence.toString(),
    'available-status': type === undefined ? undefined : type.toString(),
    breakcode: breakCode === undefined ? undefined : breakCode.toString(),
  }, false)
}

export async function setAwayFromDesk(away: boolean) {
  await handler('/Handlers/Dialogs/Presence.ashx', {
    command: 'set-away-from-desk',
    away: away.toString(),
  }, false)
}

export type PresenceDialog = {
  serverName: string
  id: string
  regionId: string
  location: model.Presence
  isIndefinite: boolean
  breakcode: number
  operatorNotes: string
  returnTime: string
  returnTimeDate?: number
  currentDeviceGroupType: number
  extendedAvailablePresence: boolean
  businessExcludeDays: string
  bstHours: number
  bstMinutes: number
}

export async function getPresence({ server, id }: model.Identifier) {
  const text = await handler('/Handlers/Dialogs/Presence.ashx', {
    command: 'get',
    servername: server,
    id: id,
    data: '0',
  }, true)
  const x: unknown[] = JSON.parse(text)
  const y = toLowerJson(x[0]) as PresenceDialog
  return { ...y, returnTimeDate: parseMsDate(y.returnTime, false) }
}

export async function setPresence({ server, id }: model.Identifier, data: PresenceDialog) {
  const returnTime = dayjs(data.returnTimeDate).format(model.SERVER_DATE_FOMATE)
  console.info('SetPresence ReturnTime ' + returnTime)
  await handler('/Handlers/Dialogs/Presence.ashx', {
    command: 'set',
    servername: server,
    id: id,
    data: JSON.stringify({ ...data, returnTime: returnTime }),
  }, false)
}

export async function setWorkTime(seconds: number) {
  await extHandler({
    command: 'setworktime',
    number: seconds.toString(),
  })
}

export async function getVoiceMail({ server, id }: model.Identifier, folder: model.VoiceMailFolder, subscription: model.EventSubscription) {
  return (await gridQuery('voicemail', folder, server, subscription, id)).rows.map<model.VoiceMail>(row => ({
    id: row.id,
    caller: {
      id: row.number,
      name: row.person,
    },
    folder,
    subject: row.subject,
    received: parseMsDate(row.sentDateTimeUtc) || 0,
    duration: parseInt(row.duration),
    status: parseInt(row.status),
    type: parseInt(row.messageType),
    urgent: row.priority !== 1,
    personal: row.forwardType === 4,
  }))
}

function getShortVmId(id: string) {
  return id.substr(0, 8)
}

function vmHandler(mailbox: model.Identifier, id: string, folder: model.VoiceMailFolder, command: string, additionalParams: Record<string, string> = {}) {
  return handler('/Handlers/UnifiedMessaging.ashx', {
    command,
    folder,
    number: getShortVmId(id),
    mailbox: mailbox.id,
    data: id,
    ...additionalParams,
  })
}

export async function playVoiceMail(mailbox: model.Identifier, id: string, folder: model.VoiceMailFolder) {
  await vmHandler(mailbox, id, folder, 'play')
}

export async function getVoiceMailAudioUrl(mailbox: model.Identifier, id: string, folder: model.VoiceMailFolder) {
  await vmHandler(mailbox, id, folder, 'flashplay')

  return getApiUrl('/Handlers/UnifiedMessaging.ashx?') + new URLSearchParams({
    command: 'getflashvoicemail',
    data: getShortVmId(id),
    data1: '1',
    'ipfx-id': ClientId!,
  })
}

export async function saveVoiceMail(mailbox: model.Identifier, id: string, folder: model.VoiceMailFolder) {
  await vmHandler(mailbox, id, folder, 'save')
}

export async function deleteVoiceMail(mailbox: model.Identifier, id: string, folder: model.VoiceMailFolder) {
  await vmHandler(mailbox, id, folder, 'delete')
}

export async function saveVoiceMails(mailbox: model.Identifier, ids: string[], folder: model.VoiceMailFolder) {
  await vmHandlers(mailbox, ids, folder, 'save')
}

export async function deleteVoiceMails(mailbox: model.Identifier, ids: string[], folder: model.VoiceMailFolder) {
  await vmHandlers(mailbox, ids, folder, 'delete')
}

function vmHandlers(mailbox: model.Identifier, ids: string[], folder: model.VoiceMailFolder, command: string) {
  return handler('/Handlers/UnifiedMessaging.ashx', {
    command,
    folder: folder.toLowerCase(),
    data: JSON.stringify(ids.map(id => getShortVmId(id))),
    mailbox: mailbox.id,
  })
}

export async function getUnreadVoiceMailCount({ server, id }: model.Identifier): Promise<number> {
  const text = await handler('/Handlers/UnifiedMessaging.ashx', {
    command: 'unread',
    server,
    number: id,
  })

  const res = JSON.parse(text)
  return parseInt(res.VoicemailNormalCount) + parseInt(res.VoicemailUrgentCount)
}

export type FavouritesRootIds = {
  extension: string
  queue: string
  contact: string
  shortcut: string
}

export async function getFavouriteFolderIds() {
  return await extHandler<FavouritesRootIds>({ command: 'favouritesrootid', number: 'queues' }, true)
}

export async function addFavourite(enity: model.Identifier, type: model.EntityType) {
  await extHandlerRaw({
    command: 'fav_a_i',
    data: JSON.stringify([[enity.id, enity.server, type, '']]),
  })
}

export async function removeFavourite(enity: model.Identifier, parendId: string, type: model.EntityType) {
  await extHandlerRaw({
    command: 'fav_d_i',
    data: JSON.stringify([[enity.id, enity.server, parendId, `${enity.server}|${enity.id}|${type}|`]]),
  })
}

export async function addFavouriteQueue(queue: model.Identifier) {
  await addFavourite(queue, model.EntityType.Queue)
}

export async function removeFavouriteQueue(queue: model.Identifier, parendId: string) {
  await removeFavourite(queue, parendId, model.EntityType.Queue)
}

export async function addFavouriteExtension(extension: model.Identifier) {
  await addFavourite(extension, model.EntityType.Extension)
}

export async function removeFavouriteExtension(extension: model.Identifier, parendId: string) {
  await removeFavourite(extension, parendId, model.EntityType.Extension)
}

export async function addFavouriteContact(extension: model.Identifier) {
  await addFavourite(extension, model.EntityType.Contact)
}

export async function removeFavouriteContact(extension: model.Identifier, parendId: string) {
  await removeFavourite(extension, parendId, model.EntityType.Contact)
}

export async function loginIntoVoicemail(extension: model.Identifier, pin: string) {
  try {
    const response = await extHandler<Result>({
      command: 'pin',
      servername: extension.server,
      extension: extension.id,
      number: pin,
      data: 'voicemail',
      entitytype: '1',
    }, false)
    return response.success
  } catch (e) {
    if (e instanceof HttpError) {
      if (e.status === 500) { return false }
    }
    throw e
  }
}

export type FatClientOperation = 'showconfiguration' | 'showqueueproperties' | 'showreports' | 'quit' | 'showextensionproperties' | 'startdesktopmonitor'
type FatClientBody = Record<string, string> & { op: FatClientOperation }

async function fatClient(body: FatClientBody) {
  const url = getUrl('/IpfxClientUi?') + new URLSearchParams(body)
  console.log('>>> ' + url)
  const resp = await ucFetch(url)

  if (!resp.ok) { throw new HttpError(resp.status, resp.statusText, await resp.text()) }
}

export async function openExtensionProperties(extension: model.Identifier) {
  await fatClient({
    op: 'showextensionproperties',
    server: extension.server,
    id: extension.id,
  })
}

export async function openQueueProperties(queue: model.Identifier) {
  await fatClient({
    op: 'showqueueproperties',
    server: queue.server,
    id: queue.id,
  })
}
export async function startDesktopMonitor(extension: model.Identifier) {
  await fatClient({
    op: 'startdesktopmonitor',
    server: extension.server,
    id: extension.id,
  })
}
export async function openAdministration() { return showFatClientUi('showconfiguration') }
export async function openReports() { return showFatClientUi('showreports') }
export function closeFatClient() { return showFatClientUi('quit') }

export async function showFatClientUi(op: FatClientOperation) {
  // the only one we cannot do is showqueueproperties because there are extras.
  await fatClient({ op })
}

export async function getExtensionDetails({ server, id }: model.Identifier): Promise<model.ExtensionDetails> {
  const [, associateNumber, mobileNumber, homeNumber, otherNumber, email, officeNumber, monitor, , , , canReceiveVoicemail, , , , , , , isMobileNumberVisible, isHomeNumberVisible, isOtherNumberVisible] = await extHandler<any[]>({ command: 'gcmi_extension', number: id, servername: server })
  return {
    associateNumber,
    email,
    homeNumber,
    mobileNumber,
    officeNumber,
    otherNumber,
    monitor: monitor === '1',
    canReceiveVoicemail: canReceiveVoicemail === '1',
    isMobileNumberVisible: isMobileNumberVisible === '1',
    isHomeNumberVisible: isHomeNumberVisible === '1',
    isOtherNumberVisible: isOtherNumberVisible === '1',
  }
}

export function getCallFolderName(folder: model.CallFolder) {
  const name =
    folder === 'placed' ? 'placedcalls' :
      folder === 'received' ? 'receivedcalls' :
        folder === 'missed' ? 'missedcalls' :
          null

  if (!name) { throw new Error('Unkown folder: ' + folder) }
  return name
}

export async function getMediaHistory(extension: string, search: string, startDate: number | null, endDate: number | null, topSearchResultCount: number, types: MediaTypesFilter, contactId?: string, identifier?: string, accountIds?: string[], agentId?: string, queueId?: string, taskId?: string) {
  /*
  if (startDate !== null) {
    console.info(dayjs(startDate).startOf('d').format(model.SERVER_DATE_FOMATE))
    console.info(dayjs(startDate).startOf('d').utc().format(model.SERVER_DATE_FOMATE))
  }

  if (endDate !== null) {
    console.info(dayjs(endDate).endOf('d').format(model.SERVER_DATE_FOMATE))
    console.info(dayjs(endDate).endOf('d').utc().format(model.SERVER_DATE_FOMATE))
  }
  */

  const searchColumns = [
    startDate !== null ? dayjs(startDate).startOf('d').utc().format(model.SERVER_DATE_FOMATE) : '',
    endDate !== null ? dayjs(endDate).endOf('d').utc().format(model.SERVER_DATE_FOMATE) : '',
    types.CALL ? types.CALL.toString() : 'false',
    types.EMAIL ? types.EMAIL.toString() : 'false',
    types.TC ? types.TC.toString() : 'false',
    types.FB ? types.FB.toString() : 'false',
    types.TWEET ? types.TWEET.toString() : 'false',
    !contactId ? '' : contactId,
    !identifier ? '' : identifier,
    types.EMAILAUTO ? 'null' : 'false',
    types.VM ? types.VM.toString() : 'false',
    JSON.stringify(accountIds),
    topSearchResultCount.toString(),
    agentId ? agentId : '',
    queueId ? queueId : '',
    taskId ? taskId : '',
    types.EMAILATTACHMENTS ? 'true' : 'null',
    types.DIRECTIONIN && types.DIRECTIONOUT ? 'null' : types.DIRECTIONOUT ? 'true' : 'false',
  ]

  const grid = await gridQuery('queues', 'media', '', model.EventSubscription.None, '', '', extension, search, -1, searchColumns)

  const rows = grid.rows.map<model.MediaCallHistoryEntry>(row => ({
    id: row.id,
    receivedAtUtc: parseMsDate(row.receivedAtUtc) || 0,
    extensionId: row.extensionId,
    extensionName: row.extensionName,
    queueId: row.queueId,
    queueName: row.queueName,
    subject: row.subject,
    conversationId: row.conversationId,
    mediaTypeId: row.mediaTypeId,
    mediaTypeName: row.mediaTypeName,
    isOutgoing: row.isOutgoing,
    isAnswered: row.isAnswered,
    folder: row.folder,
    notes: row.notes,
    taskId: row.taskId,
    lookupId: row.lookupId,
    taskSubject: row.taskSubject,

    ipfxId: row.ipfxId,
    ipfxType: row.ipfxType !== null ? parseInt(row.ipfxType) : 0,
    ipfxDescription: row.ipfxDescription,
    dataSource: row.dataSource,
    accountId: row.accountId,
  }))

  return { ...grid, rows: rows }
}

export async function getCalls(folder: model.CallFolder) {
  return (await gridQuery('callhistory', getCallFolderName(folder), '', model.EventSubscription.Add)).rows.map<model.Call>(row => ({
    id: row.id,
    from: {
      id: row.clid,
      name: row.clidDescription,
    },
    folder,
    subject: row.subject,
    date: parseMsDate(row.dateTimeStartUtc) || 0,
    duration: row.talkTime,
    read: row.read,
    mediaType: row.mediaType,
    deviceType: row.deviceType,
    callKey: row.callKey,
    mediaItemId: row.mediaItemId,
    queueId: row.queueId,
    queue: row.queue,
  }))
}


export async function setCallback(number: string) {
  await extHandlerRaw({
    command: 'setcallback',
    number,
  })
}

export function commandReceived(command: model.Command) {
  return extHandlerRaw({
    command: 'pop-received',
    number: command.RequestId,
    to: command.Header.From.Application,
    from: command.Header.To.Application,
    transaction: command.Header.TransactionId,
  })
}

export function commandAccept(command: model.Command, agentDisplayName: model.ApplicationSetting, transaction: string) {
  return sendActionCommand('pop-accept', command, agentDisplayName, transaction)
}

export function commandDecline(command: model.Command, agentDisplayName: model.ApplicationSetting, transaction: string) {
  return sendActionCommand('pop-decline', command, agentDisplayName, transaction)
}

export function commandDisconnect(command: model.Command, agentDisplayName: model.ApplicationSetting, transaction: string) {
  return sendActionCommand('pop-disconnect', command, agentDisplayName, transaction)
}

function sendActionCommand(action: string, command: model.Command, agentDisplayName: model.ApplicationSetting, transaction: string) {
  return extHandlerRaw({
    command: action,
    number: command.RequestId,
    to: command.Header.From.Application,
    from: command.Header.To.Application,
    data: JSON.stringify(command.ExtendedInfo),
    extension: agentDisplayName.value,
    transaction,
  })
}

export async function getExtraCallData(entityType: ClidObjectType, entityId: string, clid: string, clidDescription: string) {
  return await extHandler<model.ExtraCallData>({ command: 'call-data', entityType: entityType.toString(), entityId: entityId, number: clid, data: clidDescription })
}

export async function setMediaNotes(myself: model.Identifier, id: string, notes: string) {
  await extHandlerRaw({
    command: 'callnote',
    extension: myself.id,
    servername: myself.server,
    data: JSON.stringify([id, notes]),
  }, false)
}

export async function setWrapupCodes(myself: model.Identifier, extension: string, id: string, codes: string[]) {
  await extHandlerRaw({
    command: 'wrapup',
    extension: myself.id,
    servername: myself.server,
    data: JSON.stringify([id, codes.join(''), '', extension]),
  }, false)
}

interface PopUpAlert {
  Subject: string
}

export async function getMediaClid(myself: model.Identifier, mediaId: string) {
  const message = JSON.parse(await extHandlerRaw({
    command: 'clid',
    extension: myself.id,
    servername: myself.server,
    number: mediaId,
  })) as PopUpAlert

  return message.Subject
}

interface FormatedNumber {
  number: string
}

export async function getFormatedNumber(phoneNumber: string, e164: boolean, webrtc?: boolean) {
  if (!phoneNumber) { return phoneNumber }

  const message = JSON.parse(await extHandlerRaw({
    command: 'formatnumber',
    number: phoneNumber,
    data: !e164 ? 'false' : 'true',
    webrtc: !webrtc ? 'false' : 'true',
  })) as FormatedNumber

  return message.number
}

export async function getVerfiyNumber(phoneNumber: string, regionId?: string) {
  if (!phoneNumber) { return phoneNumber }

  const message = JSON.parse(await extHandlerRaw({
    command: 'verfiynumber',
    number: phoneNumber,
    regionid: regionId,
  })) as FormatedNumber

  return message.number
}


interface BooleanResult {
  result: boolean
}

export async function getAssistance(extension: string) {

  const response = JSON.parse(await extHandlerRaw({
    command: 'assistance',
  })) as BooleanResult

  return response.result
}

export async function getBookItToken() {
  const response = await extHandlerRaw({
    command: 'bookit',
  })
  return response
}

export async function requestOpenBookItWindow(type: 'request' | 'ack', clientId: string, ipAddress: string) {
  await extHandlerRaw({
    command: 'bookit-teams',
    data: type,
    number: clientId,
    transaction: ipAddress,
  })
}

export async function requestBookItUrl(type: 'request' | 'ack', clientId: string, ipAddress: string) {
  await extHandlerRaw({
    command: 'bookit-teams',
    data: type,
    number: clientId,
    transaction: ipAddress,
  })
}

export interface AsyncSoftphoneResponse extends AsyncResponse {
  data: {
    janus: {
      password: string,
      username: string,
    },
    rest: boolean,
    ipfx: {
      password: string,
      username: string,
    }
  },
}

export async function getTemporaryPasswordForSession() {
  const response = await extHandlerRaw({
    command: 'temp',
  })

  return atob(response)
}

export async function getTemporaryPasswordForWebRtc() {

  const response = parseJson<AsyncSoftphoneResponse>(await extHandlerRaw({
    command: 'softphone',
  }, false))

  return {
    success: response.success,
    error: response.error,
    data: {
      janus: {
        password: atob(response.data.janus.password),
        username: response.data.janus.username,
      },
      rest: response.data.rest,
      ipfx: {
        password: atob(response.data.ipfx.password),
        username: response.data.ipfx.username,
      },
    },
  }
}


export async function getSearchResultsByEmail(id: model.Identifier, email: string) {
  return getSearchResults(id, email, ['IContact.Email', 'IExtension.Email'])
}

export async function getSearchResultsWithEmail(myself: model.Identifier, filter: string, localServer: boolean = true, includePersonal: boolean = false) {
  return (await gridQuery('search', '', localServer ? myself.server : '*', model.EventSubscription.None, undefined, 'email', includePersonal ? myself.id : '', filter, 20, contactSearchColumns.concat(extensionSearchColumns))).rows.map<model.SearchEntity>(row => {
    const key: string[] = row.stringKey.split('|')
    const server = key[0]
    const id = key[1]
    const type = parseInt(key[2])
    const subType = key.length >= 4 ? parseInt(key[3]) : -1
    return {
      number: row.number,
      mobile: row.mobile,
      home: row.home,
      business: row.business,
      other: row.other,
      description: row.description,
      dateTimeStart: parseMsDate(row.dateTimeStart) || 0,
      data: row.data,
      matchedFieldType: parseInt(row.matchedFieldType),
      matchedValue: row.matchedValue,
      email: row.email,
      entityType: type,
      entityId: id,
      entityServer: server,
      entitySubType: subType,
    }
  })
}

export async function getSearchResultsContact(myself: model.Identifier, filter: string, emailRequired: boolean, localServer: boolean = true, includePersonal: boolean = false) {
  return (await gridQuery('search', 'contacts', localServer ? myself.server : '*', model.EventSubscription.None, undefined, emailRequired ? 'email' : '', includePersonal ? myself.id : '', filter, 20, contactSearchColumns)).rows.map<model.ContactBasic>(row => {
    const key: string[] = row.stringKey.split('|')
    const server = key[0]
    const id = key[1]
    const subType = key.length >= 4 ? parseInt(key[3]) : -1

    let extensionNumber = myself.id
    if (subType === model.ContactType.Customer) {
      extensionNumber = '-1'
    } else if (subType === model.ContactType.Company) {
      extensionNumber = '0'
    }

    return {
      id: id,
      server: server,
      regionId: '',
      extensionNumber: extensionNumber,
      description: row.description,
      home: row.home,
      business: row.business,
      mobile: row.mobile,
      email: row.email,
    }
  })
}

export async function getSearchResultsForAgentQueue(id: model.Identifier, text: string) {
  return getSearchResults(id, text, extensionSearchColumns.concat(queueSearchColumns))
}

export async function getSearchResults({ server, id }: model.Identifier, search: string, searchProperties: string[]) {
  return (await gridQuery('search', '', server, model.EventSubscription.None, undefined, undefined, id, search, 50, searchProperties)).rows.map<model.SearchEntity>(row => {
    const key: string[] = row.stringKey.split('|')
    const server = key[0]
    const id = key[1]
    const type = parseInt(key[2])
    const subType = key.length >= 4 ? parseInt(key[3]) : -1
    return {
      number: row.number,
      mobile: row.mobile,
      home: row.home,
      business: row.business,
      other: row.other,
      description: row.description,
      dateTimeStart: parseMsDate(row.dateTimeStart) || 0,
      data: row.data,
      matchedFieldType: parseInt(row.matchedFieldType),
      matchedValue: row.matchedValue,
      email: row.email,
      entityType: type,
      entityId: id,
      entityServer: server,
      entitySubType: subType,
    }
  })
}


export async function getTeamsEnhancedCti(extension: string, on?: boolean) {
  const response = await extHandlerRaw({
    command: 't_enhanced',
    number: extension,
    data: (on === false) ? "0" : (on === true) ? "1" : "",
  })
  return response.toLowerCase() === 'true'
}

export async function setDeviceGroupOffice(extension: string) {
  deviceHandlerRaw({
    command: 'set-office',
    extensionid: extension,
  })
}

export async function setDeviceGroupOther(extension: string) {
  deviceHandlerRaw({
    command: 'set-other',
    extensionid: extension,
  })
}

export async function getAgentStatusLimits(extensionId: string, subscribe: 'on' | 'off') {
  return await extHandler<model.AgentStatistic>({ command: 'limits', extension: extensionId, data: subscribe })
}

export async function screenRecordingUpload(recording: Blob, mediaId: string) {

  let data = new FormData();
  data.append('files', recording, mediaId.replace(/\|/g, '') + '.mp4')
  const url = getApiUrl('/Handlers/FileUploadHandler.ashx?type=s')
  console.log('>>> ' + url)

  const resp = await ucFetch(getApiUrl('/Handlers/FileUploadHandler.ashx?type=s'), { method: "POST", body: data })
  const text = await resp.text()
  return parseJson<UploadResponse>(text)
}

interface UploadResponse {
  Name: string
  Size: number
  ContentType: string
  FileName: string
}

interface UploadResponse {
  Name: string
  Size: number
  ContentType: string
  FileName: string
}

export async function getBookItUrl(token: string) {
  return await parseJson<AsyncBookitResponse>(await handler('/Teams/Handlers/Bookit.ashx', { command: 'none', token: token }, false))
}

